import { useEffect, useState } from "react";
import { Circle } from "lucide-react";
import { fxMarketState } from "@/lib/fxHours";

/**
 * FX / spot-metals market-hours pill. Renders green when the ~24/5 FX
 * market is open, red when it's the weekend gap.
 *
 * `variant`:
 *   - `"pill"` (default) — inline chip suitable for a page header row.
 *   - `"inline"` — no border/bg, just the dot + text (used inside cards).
 *
 * Recomputes every 30s locally — no network. Pure UTC → NY conversion
 * anchored to the 17:00 America/New_York weekly close convention.
 */
export default function FxMarketPill({
  variant = "pill",
  testid = "fx-market-pill",
}) {
  const [state, setState] = useState(() => fxMarketState());

  useEffect(() => {
    const tick = () => setState(fxMarketState());
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, []);

  const { open, pillText } = state;
  const status = open ? "open" : "closed";

  if (variant === "inline") {
    return (
      <span
        className="inline-flex items-center gap-2 font-mono text-[11px] tracking-wide"
        data-testid={`${testid}-${status}`}
        title={pillText}
      >
        <Circle
          className={
            open
              ? "h-2 w-2 animate-pulse fill-emerald-400 text-emerald-400"
              : "h-2 w-2 fill-rose-400 text-rose-400"
          }
        />
        <span
          className={
            open
              ? "text-emerald-300"
              : "text-rose-300"
          }
        >
          {pillText}
        </span>
      </span>
    );
  }

  return (
    <span
      data-testid={`${testid}-${status}`}
      title={pillText}
      className={
        open
          ? "inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-emerald-300"
          : "inline-flex items-center gap-2 rounded-full border border-rose-500/40 bg-rose-500/10 px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-rose-300"
      }
    >
      <Circle
        className={
          open
            ? "h-2 w-2 animate-pulse fill-emerald-400 text-emerald-400"
            : "h-2 w-2 fill-rose-400 text-rose-400"
        }
      />
      {pillText}
    </span>
  );
}
